/**
 * Pairings tab — Pro-only breeding-pair logbook.
 *
 * Lives inside /flock as a tab. Free users see an upgrade prompt;
 * Pro users see their pairings with embedded per-chick rows.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Plus,
  Trash2,
  Edit3,
  ChevronDown,
  ChevronRight,
  Egg,
  Sparkles,
  Calendar,
  Heart,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { usePairings } from "@/lib/pairings";
import { useFlock } from "@/lib/flock";
import { usePro } from "@/lib/pro";
import { breed as runBreed } from "@/lib/api";
import {
  PHENOTYPES,
  findActiveOption,
  summarizePhenotype,
  defaultOptions,
} from "@/lib/phenotypes";

const STATUS_OPTS = [
  { value: "alive", label: "Alive", chip: "bg-emerald-500/15 text-emerald-700" },
  { value: "sold", label: "Sold", chip: "bg-blue-500/15 text-blue-700" },
  { value: "died", label: "Died", chip: "bg-zinc-400/20 text-zinc-700" },
  { value: "unhatched", label: "Unhatched", chip: "bg-amber-500/15 text-amber-700" },
];

function StatusChip({ status }) {
  const opt = STATUS_OPTS.find((s) => s.value === status) || STATUS_OPTS[0];
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm ${opt.chip}`}>
      {opt.label}
    </span>
  );
}

function ParentChip({ parent, accent }) {
  const summary = summarizePhenotype({ sex: parent.sex, options: parent.options || {} });
  return (
    <div className={`border ${accent} bg-card px-3 py-2 rounded-sm`}>
      <div className="font-bold text-sm truncate">{parent.name}</div>
      {parent.ring_number ? (
        <div className="font-mono text-[10px] text-muted-foreground">◯ {parent.ring_number}</div>
      ) : null}
      <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{summary}</div>
    </div>
  );
}

function PairingCard({ pairing, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const eggs = pairing.eggs_laid ?? null;
  const alive = pairing.chicks.filter((c) => c.status === "alive").length;
  const total = pairing.chicks.length;

  return (
    <div
      data-testid={`pairing-card-${pairing.id}`}
      className="border border-border bg-card"
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="overline flex items-center gap-2">
              <Calendar size={11} />
              {pairing.paired_on}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 sm:gap-3 mt-2 items-stretch">
              <ParentChip parent={pairing.sire} accent="border-primary/40" />
              <div className="hidden sm:flex items-center justify-center text-muted-foreground">
                <Heart size={14} />
              </div>
              <ParentChip parent={pairing.dam} accent="border-pink-500/40" />
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              data-testid={`pairing-edit-${pairing.id}`}
              onClick={() => onEdit(pairing)}
              className="border border-border w-7 h-7 grid place-items-center rounded-sm hover:bg-secondary"
              title="Edit"
            >
              <Edit3 size={12} />
            </button>
            <button
              type="button"
              data-testid={`pairing-delete-${pairing.id}`}
              onClick={() => onDelete(pairing)}
              className="border border-border w-7 h-7 grid place-items-center rounded-sm hover:bg-destructive hover:text-destructive-foreground"
              title="Delete"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-center text-xs text-muted-foreground">
          {eggs !== null && (
            <span className="inline-flex items-center gap-1">
              <Egg size={12} /> {eggs} {eggs === 1 ? "egg" : "eggs"}
            </span>
          )}
          {total > 0 && (
            <span>
              {alive}/{total} chick{total === 1 ? "" : "s"} alive
            </span>
          )}
          {pairing.prediction_snapshot ? (
            <span className="inline-flex items-center gap-1 text-primary">
              <Sparkles size={12} /> Prediction snapshot saved
            </span>
          ) : null}
        </div>

        {pairing.notes ? (
          <div className="text-[12px] mt-2 border-l-2 border-border pl-2 italic text-muted-foreground">
            {pairing.notes}
          </div>
        ) : null}
      </div>

      <Collapsible open={expanded} onOpenChange={setExpanded} className="border-t border-border">
        <CollapsibleTrigger
          data-testid={`pairing-expand-${pairing.id}`}
          className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-secondary/50 transition-colors"
        >
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {total > 0 ? `${total} chick${total === 1 ? "" : "s"}` : "No chicks logged"}
            {pairing.prediction_snapshot ? " · Prediction" : ""}
          </div>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 space-y-4">
            {total > 0 && (
              <div className="border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/60">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium uppercase tracking-wider text-[10px]">Name</th>
                      <th className="text-left px-3 py-2 font-medium uppercase tracking-wider text-[10px]">Ring</th>
                      <th className="text-left px-3 py-2 font-medium uppercase tracking-wider text-[10px]">Sex</th>
                      <th className="text-left px-3 py-2 font-medium uppercase tracking-wider text-[10px]">Colour notes</th>
                      <th className="text-left px-3 py-2 font-medium uppercase tracking-wider text-[10px]">Hatched</th>
                      <th className="text-left px-3 py-2 font-medium uppercase tracking-wider text-[10px]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {pairing.chicks.map((c) => (
                      <tr key={c.id} data-testid={`chick-row-${c.id}`}>
                        <td className="px-3 py-2 font-medium">{c.name}</td>
                        <td className="px-3 py-2 font-mono text-[11px]">{c.ring_number || "—"}</td>
                        <td className="px-3 py-2 capitalize">
                          {c.sex === "male" ? "♂" : c.sex === "female" ? "♀" : "—"} {c.sex}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{c.colour_notes || "—"}</td>
                        <td className="px-3 py-2 font-mono text-[11px]">{c.hatched_on || "—"}</td>
                        <td className="px-3 py-2"><StatusChip status={c.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pairing.prediction_snapshot && (
              <div className="border border-border rounded-sm p-3 bg-secondary/30">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                  <Sparkles size={11} /> Prediction snapshot — {pairing.paired_on}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {pairing.prediction_snapshot.outcomes?.slice(0, 8).map((o, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px]">
                      <div
                        className="w-3 h-3 border border-border rounded-sm shrink-0"
                        style={{ backgroundColor: o.swatch }}
                      />
                      <span className="font-mono w-12 text-right text-muted-foreground">
                        {(o.probability * 100).toFixed(1)}%
                      </span>
                      <span className="truncate">{o.phenotype} ({o.sex === "male" ? "♂" : "♀"})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

const EMPTY_PARENT = (sex) => ({
  bird_id: null,
  name: "",
  ring_number: "",
  sex,
  options: defaultOptions(),
});

const EMPTY_CHICK = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  ring_number: "",
  sex: "unknown",
  colour_notes: "",
  status: "alive",
  hatched_on: "",
  notes: "",
});

function PairingFormDialog({ open, onClose, initial, onSave, saving }) {
  const { birds } = useFlock();
  const cocks = useMemo(() => birds.filter((b) => b.sex === "male"), [birds]);
  const hens = useMemo(() => birds.filter((b) => b.sex === "female"), [birds]);

  const [draft, setDraft] = useState(() => initial || {
    sire: EMPTY_PARENT("male"),
    dam: EMPTY_PARENT("female"),
    paired_on: new Date().toISOString().slice(0, 10),
    eggs_laid: "",
    chicks: [],
    prediction_snapshot: null,
    notes: "",
  });
  const [predicting, setPredicting] = useState(false);

  // Reset draft when opening with a different pairing
  const dialogKey = initial?.id || "new";
  if ((draft.__key || "") !== dialogKey) {
    setDraft({
      ...(initial || {
        sire: EMPTY_PARENT("male"),
        dam: EMPTY_PARENT("female"),
        paired_on: new Date().toISOString().slice(0, 10),
        eggs_laid: "",
        chicks: [],
        prediction_snapshot: null,
        notes: "",
      }),
      eggs_laid: initial?.eggs_laid ?? "",
      notes: initial?.notes ?? "",
      chicks: (initial?.chicks || []).map((c) => ({
        ...c,
        ring_number: c.ring_number || "",
        colour_notes: c.colour_notes || "",
        hatched_on: c.hatched_on || "",
        notes: c.notes || "",
      })),
      __key: dialogKey,
    });
  }

  const setParentFromBird = (role, bird) => {
    if (!bird) {
      setDraft((d) => ({ ...d, [role]: EMPTY_PARENT(role === "sire" ? "male" : "female") }));
      return;
    }
    setDraft((d) => ({
      ...d,
      [role]: {
        bird_id: bird.id,
        name: bird.name,
        ring_number: bird.ring_number || "",
        sex: role === "sire" ? "male" : "female",
        options: bird.options || defaultOptions(),
      },
    }));
  };

  const setParentField = (role, field, value) => {
    setDraft((d) => ({ ...d, [role]: { ...d[role], [field]: value, bird_id: null } }));
  };

  const addChick = () => {
    setDraft((d) => ({ ...d, chicks: [...d.chicks, EMPTY_CHICK()] }));
  };

  const updateChick = (idx, field, value) => {
    setDraft((d) => ({
      ...d,
      chicks: d.chicks.map((c, i) => (i === idx ? { ...c, [field]: value } : c)),
    }));
  };

  const removeChick = (idx) => {
    setDraft((d) => ({ ...d, chicks: d.chicks.filter((_, i) => i !== idx) }));
  };

  const snapshotPrediction = async () => {
    if (!draft.sire.name || !draft.dam.name) {
      toast.error("Set sire and dam first.");
      return;
    }
    setPredicting(true);
    try {
      const data = await runBreed({
        male: { sex: "male", name: draft.sire.name, options: draft.sire.options },
        female: { sex: "female", name: draft.dam.name, options: draft.dam.options },
      });
      setDraft((d) => ({ ...d, prediction_snapshot: data }));
      toast.success("Prediction snapshot captured.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not snapshot prediction.");
    } finally {
      setPredicting(false);
    }
  };

  const handleSave = () => {
    if (!draft.sire.name?.trim()) return toast.error("Sire name is required.");
    if (!draft.dam.name?.trim()) return toast.error("Dam name is required.");
    if (!draft.paired_on) return toast.error("Pair date is required.");

    const payload = {
      sire: {
        bird_id: draft.sire.bird_id,
        name: draft.sire.name.trim(),
        ring_number: draft.sire.ring_number?.trim() || null,
        sex: "male",
        options: draft.sire.options || {},
      },
      dam: {
        bird_id: draft.dam.bird_id,
        name: draft.dam.name.trim(),
        ring_number: draft.dam.ring_number?.trim() || null,
        sex: "female",
        options: draft.dam.options || {},
      },
      paired_on: draft.paired_on,
      eggs_laid: draft.eggs_laid === "" || draft.eggs_laid === null ? null : Number(draft.eggs_laid),
      chicks: draft.chicks.map((c) => ({
        id: c.id?.startsWith("tmp-") ? null : c.id,
        name: c.name.trim() || `Chick`,
        ring_number: c.ring_number?.trim() || null,
        sex: c.sex,
        colour_notes: c.colour_notes?.trim() || null,
        status: c.status,
        hatched_on: c.hatched_on?.trim() || null,
        notes: c.notes?.trim() || null,
      })),
      prediction_snapshot: draft.prediction_snapshot,
      notes: draft.notes?.trim() || null,
    };
    onSave(payload, initial?.id || null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="pairing-form-dialog"
        className="sm:max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{initial ? "Edit pairing" : "Log a new pairing"}</DialogTitle>
          <DialogDescription>
            Pick the parents from your flock or enter them ad-hoc. Snapshot
            the Calculator prediction so you can compare predicted vs actual
            outcomes once the chicks hatch.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-2">
          {[
            { role: "sire", label: "Sire ♂ (Cock)", flockList: cocks, accent: "border-primary/50" },
            { role: "dam",  label: "Dam ♀ (Hen)",   flockList: hens,  accent: "border-pink-500/50" },
          ].map(({ role, label, flockList, accent }) => {
            const parent = draft[role];
            const isFlockLinked = !!parent.bird_id;
            return (
              <div key={role} className={`border ${accent} rounded-sm p-3`}>
                <div className="overline mb-2">{label}</div>
                <select
                  data-testid={`pairing-${role}-select`}
                  value={parent.bird_id || ""}
                  onChange={(e) => {
                    const bird = flockList.find((b) => b.id === e.target.value);
                    setParentFromBird(role, bird);
                  }}
                  className="w-full border border-border bg-background px-2 py-1.5 rounded-sm text-sm mb-2"
                >
                  <option value="">— Ad-hoc bird (enter manually) —</option>
                  {flockList.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}{b.ring_number ? ` · ${b.ring_number}` : ""}
                    </option>
                  ))}
                </select>
                <input
                  data-testid={`pairing-${role}-name`}
                  value={parent.name}
                  onChange={(e) => setParentField(role, "name", e.target.value)}
                  placeholder="Name (e.g. Bluey)"
                  maxLength={60}
                  disabled={isFlockLinked}
                  className="w-full border border-border bg-background px-2 py-1.5 rounded-sm text-sm mb-2 disabled:opacity-60"
                />
                <input
                  data-testid={`pairing-${role}-ring`}
                  value={parent.ring_number}
                  onChange={(e) => setParentField(role, "ring_number", e.target.value)}
                  placeholder="Ring number (optional)"
                  maxLength={40}
                  disabled={isFlockLinked}
                  className="w-full border border-border bg-background px-2 py-1.5 rounded-sm text-sm font-mono disabled:opacity-60"
                />
                <div className="text-[10px] text-muted-foreground mt-2 leading-snug">
                  {isFlockLinked
                    ? "Phenotype loaded from your flock."
                    : "Tip: save this bird in My Flock first to record its phenotype. Ad-hoc rows are stored as plain entries."}
                </div>
                {parent.options && Object.keys(parent.options).length > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {summarizePhenotype({ sex: parent.sex, options: parent.options })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-border pt-3">
          <label className="block text-xs uppercase tracking-wider text-muted-foreground">
            Paired on
            <input
              data-testid="pairing-paired-on"
              type="date"
              value={draft.paired_on}
              onChange={(e) => setDraft({ ...draft, paired_on: e.target.value })}
              className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
            />
          </label>
          <label className="block text-xs uppercase tracking-wider text-muted-foreground">
            Eggs laid <span className="lowercase tracking-normal text-muted-foreground/70">(optional)</span>
            <input
              data-testid="pairing-eggs"
              type="number"
              min={0}
              max={20}
              value={draft.eggs_laid}
              onChange={(e) => setDraft({ ...draft, eggs_laid: e.target.value })}
              className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              data-testid="pairing-snapshot-prediction"
              onClick={snapshotPrediction}
              disabled={predicting}
              className="w-full border border-border px-3 py-2 rounded-sm text-sm hover:bg-secondary inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {predicting ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
              {draft.prediction_snapshot ? "Re-snapshot prediction" : "Snapshot prediction"}
            </button>
          </div>
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Chicks ({draft.chicks.length})
            </div>
            <button
              type="button"
              data-testid="pairing-add-chick"
              onClick={addChick}
              className="text-xs border border-border px-2.5 py-1 rounded-sm hover:bg-secondary inline-flex items-center gap-1"
            >
              <Plus size={12} /> Add chick
            </button>
          </div>
          {draft.chicks.length === 0 ? (
            <div className="border border-dashed border-border rounded-sm p-4 text-center text-xs text-muted-foreground">
              No chicks yet. Add one when they hatch.
            </div>
          ) : (
            <div className="border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-secondary/60">
                  <tr>
                    <th className="text-left px-2 py-2 font-medium uppercase tracking-wider text-[10px]">Name</th>
                    <th className="text-left px-2 py-2 font-medium uppercase tracking-wider text-[10px]">Ring</th>
                    <th className="text-left px-2 py-2 font-medium uppercase tracking-wider text-[10px]">Sex</th>
                    <th className="text-left px-2 py-2 font-medium uppercase tracking-wider text-[10px]">Colour notes</th>
                    <th className="text-left px-2 py-2 font-medium uppercase tracking-wider text-[10px]">Hatched</th>
                    <th className="text-left px-2 py-2 font-medium uppercase tracking-wider text-[10px]">Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {draft.chicks.map((c, idx) => (
                    <tr key={c.id} data-testid={`chick-edit-row-${idx}`}>
                      <td className="px-2 py-1">
                        <input
                          value={c.name}
                          onChange={(e) => updateChick(idx, "name", e.target.value)}
                          placeholder="Chick name"
                          className="w-full border border-border bg-background px-2 py-1 rounded-sm text-xs"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          value={c.ring_number}
                          onChange={(e) => updateChick(idx, "ring_number", e.target.value)}
                          placeholder="Ring"
                          className="w-full border border-border bg-background px-2 py-1 rounded-sm text-xs font-mono"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={c.sex}
                          onChange={(e) => updateChick(idx, "sex", e.target.value)}
                          className="w-full border border-border bg-background px-2 py-1 rounded-sm text-xs"
                        >
                          <option value="unknown">?</option>
                          <option value="male">♂ male</option>
                          <option value="female">♀ female</option>
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <input
                          value={c.colour_notes}
                          onChange={(e) => updateChick(idx, "colour_notes", e.target.value)}
                          placeholder="e.g. Cobalt opaline"
                          className="w-full border border-border bg-background px-2 py-1 rounded-sm text-xs"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="date"
                          value={c.hatched_on}
                          onChange={(e) => updateChick(idx, "hatched_on", e.target.value)}
                          className="w-full border border-border bg-background px-2 py-1 rounded-sm text-xs"
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={c.status}
                          onChange={(e) => updateChick(idx, "status", e.target.value)}
                          className="w-full border border-border bg-background px-2 py-1 rounded-sm text-xs"
                        >
                          {STATUS_OPTS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1">
                        <button
                          type="button"
                          onClick={() => removeChick(idx)}
                          className="border border-border w-6 h-6 grid place-items-center rounded-sm hover:bg-destructive hover:text-destructive-foreground"
                        >
                          <Trash2 size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <label className="block text-xs uppercase tracking-wider text-muted-foreground mt-3">
          Notes <span className="lowercase tracking-normal text-muted-foreground/70">(optional)</span>
          <textarea
            data-testid="pairing-notes"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            rows={2}
            maxLength={600}
            placeholder="e.g. First clutch of the season. Hen plucking again."
            className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
          />
        </label>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            data-testid="pairing-form-save"
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="bg-foreground text-background px-4 py-2 rounded-sm text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : null}
            {saving ? "Saving…" : initial ? "Update pairing" : "Log pairing"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PairingsTab() {
  const { isPro } = usePro();
  const { pairings, loading, error, create, update, remove } = usePairings();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!isPro) {
    return (
      <div
        data-testid="pairings-pro-gate"
        className="border border-dashed border-border bg-card p-12 text-center"
      >
        <Lock size={28} className="mx-auto text-muted-foreground mb-3" />
        <div className="font-bold text-lg">Pair History is a Pro feature</div>
        <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
          Log every pairing, snapshot the Calculator prediction, and track each
          chick&apos;s ring number, colour, and survival status across breeding
          seasons.
        </p>
        <Link
          to="/pro"
          data-testid="pairings-upgrade-cta"
          className="mt-4 inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-sm font-medium"
        >
          <Sparkles size={14} /> Unlock Pro · A$14.99
        </Link>
      </div>
    );
  }

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (pairing) => {
    setEditing(pairing);
    setDialogOpen(true);
  };

  const onSave = async (payload, id) => {
    setSaving(true);
    try {
      if (id) {
        await update(id, payload);
        toast.success("Pairing updated");
      } else {
        await create(payload);
        toast.success("Pairing logged");
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not save pairing.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (pairing) => {
    const label = `${pairing.sire.name} × ${pairing.dam.name} (${pairing.paired_on})`;
    if (!window.confirm(`Delete pairing "${label}"? This cannot be undone.`)) return;
    try {
      await remove(pairing.id);
      toast.success("Pairing deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete pairing.");
    }
  };

  return (
    <div data-testid="pairings-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">
          {pairings.length} pairing{pairings.length === 1 ? "" : "s"} logged
        </div>
        <button
          type="button"
          data-testid="add-pairing-btn"
          onClick={openAdd}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium"
        >
          <Plus size={14} /> Log pairing
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">
          <Loader2 className="mx-auto animate-spin mb-2" size={20} />
          Loading pairings…
        </div>
      ) : error ? (
        <div className="border border-destructive/40 bg-destructive/5 text-destructive p-4 rounded-sm text-sm">
          {error}
        </div>
      ) : pairings.length === 0 ? (
        <div
          data-testid="pairings-empty"
          className="border border-dashed border-border bg-card p-12 text-center"
        >
          <Heart size={28} className="mx-auto text-muted-foreground mb-3" />
          <div className="font-bold text-lg">No pairings logged yet</div>
          <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
            Log a pairing when you put a cock and hen together — pick birds
            from My Flock or enter ad-hoc parents.
          </p>
          <button
            type="button"
            data-testid="add-first-pairing-btn"
            onClick={openAdd}
            className="mt-4 inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-sm font-medium"
          >
            <Plus size={14} /> Log your first pairing
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {pairings.map((p) => (
            <PairingCard
              key={p.id}
              pairing={p}
              onEdit={openEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      <PairingFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={editing}
        onSave={onSave}
        saving={saving}
      />
    </div>
  );
}
